import { useEffect, useRef } from 'react';

/**
 * Native BackButton only paints inside the Telegram client.
 * telegram-web-app.js is also loaded on the standalone site and exposes a
 * BackButton stub that never appears, so the object existing is not enough —
 * same rule as CloudStorage (non-empty initData).
 */
export const hasTelegramBackButton = () =>
  !!window.Telegram?.WebApp?.initData && !!window.Telegram?.WebApp?.BackButton;

// A stack of back handlers — the topmost (most recently mounted screen) wins.
// This is robust to overlapping mounts: when a screen slides out while another
// slides in, removing the old handler still leaves the new one showing.
const stack: Array<() => void> = [];
let bound = false;

function onClick() {
  const top = stack[stack.length - 1];
  if (top) top();
}

function sync() {
  const bb = hasTelegramBackButton() ? window.Telegram?.WebApp?.BackButton : undefined;
  if (!bb) return;
  if (stack.length > 0) {
    if (!bound) {
      bb.onClick(onClick);
      bound = true;
    }
    bb.show();
  } else {
    bb.hide();
    if (bound) {
      bb.offClick(onClick);
      bound = false;
    }
  }
}

/**
 * Show Telegram's native BackButton while this screen is mounted and route its
 * tap to `onBack`. Uses a shared stack so multiple/overlapping screens behave
 * correctly. No-op outside Telegram.
 */
export function useBackButton(onBack: () => void, enabled = true) {
  const ref = useRef(onBack);
  ref.current = onBack;

  useEffect(() => {
    if (!enabled) return;
    const handler = () => ref.current();
    stack.push(handler);
    sync();
    return () => {
      const i = stack.indexOf(handler);
      if (i >= 0) stack.splice(i, 1);
      sync();
    };
  }, [enabled]);
}
